import React, {Fragment, useEffect, useState} from "react";
import clsx from "clsx";
import {capitalize} from "helpers/capitalize";
import {Button, Dropdown} from "flowbite-react";
import Chrome, {ChromeInputType} from "@uiw/react-color-chrome";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faPlus, faX} from "@fortawesome/free-solid-svg-icons";
import {calculateBackgroundColor} from "helpers/color-calculator";
import {BRAND_TRAIT_DEFINITIONS, BrandTraitKey} from "data/models/shared/bc-sheet/brand-trait";
import {defaultFonts} from "data/definition/defaults/fonts";
import {COLOR_DEFINITION_KEYS, ColorDefinition, ColorDefinitionKey} from "data/models/shared/recommended-colors3";

type Traits = {
  primary: BrandTraitKey;
  secondary: BrandTraitKey;
}

type ColorPickingComponentProps = {
  definedColors: ColorDefinition[];
  onSave: (colors: ColorDefinition[]) => Promise<void>;
  onClose: () => void;
  traits: Traits;
  getTraitColors: (primaryTraitKey: BrandTraitKey, secondaryTraitKey: BrandTraitKey) => ColorDefinition[];
}

type ColorToSelectProps = {
  colorToSelect: ColorDefinitionKey;
}

export default function ColorPickingComponent(
    {
      definedColors,
      onSave,
      onClose,
      traits,
      getTraitColors,
    }: ColorPickingComponentProps
) {

  const [selectedColors, setSelectedColors] = useState<ColorDefinition[]>([...definedColors]);
  const [selectedColorIndex, setSelectedColorIndex] = useState<number | null>(definedColors.length !== 0 ? 0 : null);
  const [primaryBrandTraitToCopy, setPrimaryBrandTraitToCopy] = useState<BrandTraitKey | null>(null);
  const [secondaryBrandTraitToCopy, setSecondaryBrandTraitToCopy] = useState<BrandTraitKey | null>(null);

  useEffect(() => {
    const colorsToUse = selectedColorIndex !== null ? selectedColors[selectedColorIndex] : defaultUserSurveyData.colors;
    [
      ["--wp-color-primary", colorsToUse.primary],
      ["--wp-color-secondary", colorsToUse.secondary],
      ["--wp-color-accent", colorsToUse.accent],
      ["--wp-color-background", calculateBackgroundColor(colorsToUse.primary)],
      ["--wp-family-brand", defaultUserSurveyData.fonts.brandFont.name],
      ["--wp-family-sub-title", defaultUserSurveyData.fonts.headline.name],
      ["--wp-family-heading", defaultUserSurveyData.fonts.headline.name],
      ["--wp-family-sub-heading", defaultUserSurveyData.fonts.headline.name],
      ["--wp-family-section-header", defaultUserSurveyData.fonts.headline.name],
      ["--wp-family-body", defaultUserSurveyData.fonts.body.name],
      ["--wp-family-quote", defaultUserSurveyData.fonts.quote.name],
      ["--wp-family-caption", defaultUserSurveyData.fonts.caption.name],
      ["--wp-weight-brand", (defaultUserSurveyData.fonts.brandFont.weight ?? 400).toString()],
      ["--wp-weight-sub-title", (defaultUserSurveyData.fonts.headline.weight ?? 400).toString()],
      ["--wp-weight-heading", (defaultUserSurveyData.fonts.headline.weight ?? 400).toString()],
      ["--wp-weight-sub-heading", (defaultUserSurveyData.fonts.headline.weight ?? 400).toString()],
      ["--wp-weight-section-header", (defaultUserSurveyData.fonts.headline.weight ?? 400).toString()],
      ["--wp-weight-body", (defaultUserSurveyData.fonts.body.weight ?? 400).toString()],
      ["--wp-weight-quote", (defaultUserSurveyData.fonts.quote.weight ?? 400).toString()],
      ["--wp-weight-caption", (defaultUserSurveyData.fonts.caption.weight ?? 400).toString()],
    ].forEach(([cssVar, cssValue]) => document.documentElement.style.setProperty(cssVar, cssValue));
  }, [selectedColors, selectedColorIndex]);

  function ColorToSelect({colorToSelect}: ColorToSelectProps) {
    const selectedColorHex = selectedColorIndex !== null ? selectedColors[selectedColorIndex][colorToSelect] : null;
    return <div className={"flex flex-col items-center"}>
      <div className={"text-left ml-1"}>
      <span
          className={"text-neutral-500 text-sm font-bold"}
      >{capitalize(colorToSelect)}</span>
      </div>
      <div
          className={"grow m-1 rounded-md h-12 w-full"}
          style={{
            backgroundColor: selectedColorHex ?? "transparent",
          }}></div>
      <div
          className={"mx-4 mb-2 w-full text-neutral-500 text-sm text-left"}
      >
        {selectedColorHex?.toUpperCase() ?? ""}&nbsp;
      </div>
    </div>
  }

  function handleColorChange(colorDefinitionKey: ColorDefinitionKey, color: string) {
    if (selectedColorIndex === null) {
      throw new Error("Unexpected condition: selectedColorIndex === null");
    }
    const changedColors = [...selectedColors];
    changedColors[selectedColorIndex][colorDefinitionKey] = color;
    setSelectedColors(
        changedColors
    );
  }

  function removeColor(index: number) {
    setSelectedColors([
      ...selectedColors.slice(0, index),
      ...selectedColors.slice(index + 1)
    ]);
    if (selectedColors.length === 1) {
      setSelectedColorIndex(null);
    } else if (selectedColorIndex === selectedColors.length - 1) {
      setSelectedColorIndex(selectedColors.length - 2);
    }
  }

  function addColor() {
    setSelectedColors([
      ...selectedColors, {
        primary: "#607080",
        secondary: "#A0B0C0",
        accent: "#304050",
      }
    ]);
    setSelectedColorIndex(selectedColors.length);
  }

  function saveColorsAndClose() {
    onSave(selectedColors).then(() => onClose());
  }

  function resetAndClose() {
    setSelectedColors([...definedColors]);
    onClose();
  }

  function copySelectedTraitColors() {
    if (primaryBrandTraitToCopy === null || secondaryBrandTraitToCopy === null) {
      return;
    }
    setSelectedColors([...getTraitColors(primaryBrandTraitToCopy, secondaryBrandTraitToCopy)]);
  }

  return <div className={"flex flex-row bg-neutral-50 p-8 min-w-96 rounded-2xl"}>
    <div className={"flex flex-col"}>
      <div className="flex flex-row gap-4 mb-4 justify-center text-xl text-neutral-500">
        <div>Primary: <span className={"font-bold"}>{traits.primary}</span></div>
        <div>Secondary: <span className={"font-bold"}>{traits.secondary}</span></div>
      </div>
      <div className={"flex flex-row gap-4 my-8"}>
        <div className={"flex flex-col justify-center"}>Copy From:</div>
        <Dropdown
            label={`Primary trait: ${primaryBrandTraitToCopy ? BRAND_TRAIT_DEFINITIONS[primaryBrandTraitToCopy].title : ""}`}
            dismissOnClick={true}
        >
          {(Object.keys(BRAND_TRAIT_DEFINITIONS) as BrandTraitKey[]).map((primaryKey, index) =>
              <Dropdown.Item
                  key={index}
                  value={primaryKey}
                  onClick={() => setPrimaryBrandTraitToCopy(primaryKey)}
              >{BRAND_TRAIT_DEFINITIONS[primaryKey].title}</Dropdown.Item>
          )}
        </Dropdown>
        <Dropdown
            label={`Secondary trait: ${secondaryBrandTraitToCopy ? BRAND_TRAIT_DEFINITIONS[secondaryBrandTraitToCopy].title : ""}`}
            dismissOnClick={true}
        >
          {(Object.keys(BRAND_TRAIT_DEFINITIONS) as BrandTraitKey[]).map((secondaryKey, index) =>
              <Dropdown.Item
                  key={index}
                  value={secondaryKey}
                  onClick={() => setSecondaryBrandTraitToCopy(secondaryKey)}
              >{BRAND_TRAIT_DEFINITIONS[secondaryKey].title}</Dropdown.Item>
          )}
        </Dropdown>
        <Button
            color={"blue"}
            onClick={() => copySelectedTraitColors()}
        >
          Copy Colors
        </Button>
      </div>
      <div className="flex flex-row gap-4 justify-center">
        <div className={"flex flex-col gap-1"}>
          {selectedColors?.map((color, index) => <Fragment key={index}>
            <div
                className={clsx(
                    "h-12 flex flex-row border-2 p-1 rounded-md",
                    selectedColorIndex === index ? "border-neutral-500" : "border-transparent",
                )}
            >
              <div
                  className={"flex flex-row cursor-pointer"}
                  onClick={() => setSelectedColorIndex(index)}
              >
                {COLOR_DEFINITION_KEYS.map((colorDefinitionKey, index) =>
                    <div key={index} className={"min-w-16 h-full"} style={{
                      backgroundColor: color[colorDefinitionKey],
                    }}/>
                )}
                <div className={"min-w-16 h-full"} style={{
                  backgroundColor: calculateBackgroundColor(color.primary),
                }}/>
              </div>
              <div className={"ml-1 flex flex-col justify-center"}>
                <Button
                    color={"blue"}
                    onClick={() => removeColor(index)}
                >
                  <FontAwesomeIcon icon={faX} color={"white"} size={"xs"}/>
                </Button>
              </div>
            </div>
          </Fragment>)}
          <div className={"flex flex-row justify-center"}>
            <Button
                color={"blue"}
                onClick={() => addColor()}
            >
              <FontAwesomeIcon icon={faPlus} color={"white"} size={"2xl"}/>
            </Button>
          </div>
        </div>
        {selectedColorIndex !== null && COLOR_DEFINITION_KEYS.map((colorDefinitionKey, index) =>
            <div key={index} className={"flex flex-col"}>
              <ColorToSelect colorToSelect={colorDefinitionKey}/>
              <Chrome
                  color={selectedColors[selectedColorIndex][colorDefinitionKey] === "#00000000" ? "#304050" : selectedColors[selectedColorIndex][colorDefinitionKey]}
                  inputType={ChromeInputType.HEXA}
                  // style={{ marginTop: 10, width: 140 }}
                  // placement={GithubPlacement.TopRight}
                  showEyeDropper={true}
                  showColorPreview={true}
                  showEditableInput={true}
                  showAlpha={false}
                  onChange={(color) => {
                    handleColorChange(colorDefinitionKey, color.hex);
                  }}
              />
            </div>)}
      </div>
      <div className={"mt-4 flex flex-row gap-4 justify-center"}>
        <div>
          <Button
              color={"blue"}
              className={"mb-2"}
              onClick={() => saveColorsAndClose()}
          >
            Save
          </Button>
        </div>
        <div>
          <Button
              color={"grey"}
              className={"mb-2"}
              onClick={() => resetAndClose()}
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
    <div className={"ml-8 min-w-96 bg-white"}>

    </div>
  </div>
}


const defaultUserSurveyData = {
  businessName: "Business Name",
  fonts: {
    brandFont: defaultFonts.brandFont,
    body: defaultFonts.bodyFont,
    headline: defaultFonts.headlineFont,
    quote: defaultFonts.quoteFont,
    caption: defaultFonts.captionFont,
  },
  colors: {
    primary: "#666666",
    secondary: "#999999",
    accent: "#000000",
    background: "#D8D8D8",
  },
  websiteHeadline: "Your headline",
  keyPhoto: "/brandingcompass-r-logo.png",
  paragraphBlock: "Details about business.\n\n" +
      "Details about business",
  writeKeyMessage: "Key business message.",
  contactDetails: {
    cityStateZip: "Town, ST 00000",
    hours: {
      friday: "8 am - 8 pm",
      monday: "",
      saturday: "10 am - 10 pm",
      sunday: "Closed",
      thursday: "",
      tuesday: "",
      wednesday: "",
    },
    phoneNumber: "0000000000",
    socialMedia: {
      facebookUrl: "facebook",
      instagramUrl: "instagram",
      linkedinUrl: "linkedin",
      tikTokUrl: "tiktok",
      twitterUrl: "twitter",
      youtubeUrl: "you tube",
    },
    streetAddress: "152 Mountain Rd",
  },
};

